<script setup lang="ts">
  import { computed, ref, watch, onMounted, onUnmounted } from 'vue'
  import { FileText, Calendar, Square, PenTool, CircleAlert } from 'lucide-vue-next'
  import { type FormField, FormFieldTypeEnum } from '@/types/generated'
  import VueDraggableResizable from 'vue-draggable-resizable'
  import 'vue-draggable-resizable/style.css'
  import DatePickerField from '@/components/admin/form/DatePickerField.vue'
  import CheckboxField from '@/components/admin/form/CheckboxField.vue'
  import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/components/ui/tooltip'

  interface Props {
    field: FormField
    zoomLevel: number
    isSelected: boolean
    isEditable?: boolean
    preventDeactivation?: boolean
    isFillable?: boolean
    fieldValue?: any
    signatureData?: string
    isFilledBy?: string
    validationError?: string
  }

  const props = withDefaults(defineProps<Props>(), {
    isEditable: true,
    preventDeactivation: false,
    isFillable: false,
    fieldValue: '',
    signatureData: '',
    validationError: undefined
  })

  const emit = defineEmits<{
    (e: 'click', field: FormField): void
    (e: 'move', field: FormField, newPosition: { x: number; y: number }): void
    (e: 'resize', field: FormField, newSize: { width: number; height: number }): void
    (e: 'resizestart', field: FormField): void
    (e: 'resizeend', field: FormField): void
    (e: 'delete', field: FormField): void
    (e: 'dragstart', field: FormField): void
    (e: 'dragmove', field: FormField): void
    (e: 'dragend'): void
    (e: 'field-value-change', fieldId: string, value: any): void
    (e: 'signature-click', field: FormField): void
  }>()

  // Icon mapping for different field types
  const fieldIcons = {
    [FormFieldTypeEnum.TEXTINPUT]: FileText,
    [FormFieldTypeEnum.TEXTAREA]: FileText,
    [FormFieldTypeEnum.DATEFIELD]: Calendar,
    [FormFieldTypeEnum.CHECKBOX]: Square,
    [FormFieldTypeEnum.SIGNATURE]: PenTool
  }

  const draggableResizableRef = ref<InstanceType<typeof VueDraggableResizable> | null>(null)

  const elementX = ref(props.field.coordinates.x * props.zoomLevel)
  const elementY = ref(props.field.coordinates.y * props.zoomLevel)
  const elementWidth = ref(props.field.coordinates.width * props.zoomLevel)
  const elementHeight = ref(props.field.coordinates.height * props.zoomLevel)
  const elementActive = ref(props.isSelected)

  const hasValidationError = computed(() => {
    return !!props.validationError
  })

  const shouldShowErrorIcon = computed(() => {
    return !!(props.validationError && props.isFillable)
  })

  const isRequiredAndEmpty = computed(() => {
    if (!props.field.required || !props.isFillable) return false

    if (props.field.type === FormFieldTypeEnum.CHECKBOX) {
      return props.fieldValue === false || props.fieldValue === 'false' || !props.fieldValue
    }

    if (props.field.type === FormFieldTypeEnum.SIGNATURE) {
      return !props.signatureData && !props.fieldValue
    }

    return !props.fieldValue || props.fieldValue === ''
  })

  const isShiftPressed = ref(false)
  const dragAxis = ref<'both' | 'x' | 'y'>('both')
  const initialDragPosition = ref<{ x: number; y: number } | null>(null)

  // Color classes based on who can edit the field
  const fieldColorClasses = computed(() => {
    const baseClasses =
      'absolute border-2 rounded flex items-center px-2 hover:opacity-90 transition-all duration-200 select-none'

    switch (props.field.assigned_to) {
      case 'lawyer':
        return `${baseClasses} border-blue-500 bg-blue-50`
      case 'client':
        return `${baseClasses} border-purple-500 bg-purple-50`
      default:
        return `${baseClasses} border-gray-500 bg-gray-50`
    }
  })

  // Get the appropriate icon component
  const IconComponent = computed(() => fieldIcons[props.field.type] || FileText)

  // Handle classes for resize handles
  const handleClasses = computed(() => [
    'absolute h-3 w-3 rounded-sm border border-white shadow-sm transition-all duration-150 hover:scale-110',
    {
      'bg-blue-500 hover:bg-blue-600': props.field.assigned_to === 'lawyer',
      'bg-purple-500 hover:bg-purple-600': props.field.assigned_to === 'client',
      'bg-gray-500 hover:bg-gray-600': !['lawyer', 'client'].includes(props.field.assigned_to)
    }
  ])

  const displayText = computed(() => {
    return props.field.placeholder || ''
  })

  const shouldShowText = computed(() => {
    return !!props.field.placeholder
  })

  // Consolidated computed styles that scale with zoomLevel
  const scaledStyles = computed(() => {
    const zoom = props.zoomLevel
    const baseFontSize = 12 // text-xs = 12px
    const smallFontSize = 10 // For date fields
    const iconSize = 12 // Icon size in pixels
    const baseCheckboxSize = 20 // h-5 w-5

    // Calculate checkbox size that fits within field
    const scaledCheckboxSize = baseCheckboxSize * zoom
    const fieldHeight = props.field.coordinates.height * zoom
    const fieldWidth = props.field.coordinates.width * zoom
    const maxCheckboxSize = Math.min(fieldHeight, fieldWidth) * 0.8
    const checkboxSize = Math.max(Math.min(scaledCheckboxSize, maxCheckboxSize), 12)

    return {
      fontSize: `${baseFontSize * zoom}px`,
      smallFontSize: `${smallFontSize * zoom}px`,
      lineHeight: `${1.5 * zoom}`,
      iconSize: `${iconSize * zoom}px`,
      iconSpacing: `${(iconSize + 8) * zoom}px`, // Icon size + 8px spacing
      checkboxSize: `${checkboxSize}px`,
      checkboxScale: checkboxSize / baseCheckboxSize
    }
  })

  // Derived style objects for specific use cases
  const textInputStyle = computed(() => ({
    fontSize: scaledStyles.value.fontSize,
    lineHeight: scaledStyles.value.lineHeight
  }))

  const overlayTextStyle = computed(() => ({
    fontSize: scaledStyles.value.fontSize
  }))

  const dateFieldContainerStyle = computed(() => ({
    '--date-field-font-size': scaledStyles.value.smallFontSize,
    '--date-field-line-height': scaledStyles.value.lineHeight,
    '--date-field-icon-size': scaledStyles.value.iconSize,
    '--date-field-icon-spacing': scaledStyles.value.iconSpacing
  }))

  const checkboxStyle = computed(() => ({
    fontSize: scaledStyles.value.fontSize,
    '--checkbox-size': scaledStyles.value.checkboxSize,
    '--checkbox-scale': scaledStyles.value.checkboxScale,
    '--checkbox-font-size': scaledStyles.value.fontSize
  }))

  // Get size constraints based on field type (reactive)
  const fieldSize = computed(() => {
    switch (props.field.type) {
      case FormFieldTypeEnum.TEXTINPUT:
        return {
          minWidth: 30,
          minHeight: 10,
          maxWidth: 400,
          maxHeight: 40 // Single line text field
        }
      case FormFieldTypeEnum.TEXTAREA:
        return {
          minWidth: 30,
          minHeight: 10,
          maxWidth: 600,
          maxHeight: 200 // Multi-line text area
        }
      case FormFieldTypeEnum.DATEFIELD:
        return {
          minWidth: 30,
          minHeight: 10,
          maxWidth: 200,
          maxHeight: 40 // Single line date field
        }
      case FormFieldTypeEnum.CHECKBOX:
        return {
          minWidth: 20,
          minHeight: 10,
          maxWidth: 30,
          maxHeight: 30 // Small checkbox
        }
      case FormFieldTypeEnum.SIGNATURE:
        return {
          minWidth: 30,
          minHeight: 10,
          maxWidth: 500,
          maxHeight: 150 // Signature area
        }
      default:
        return {
          minWidth: 30,
          minHeight: 10,
          maxWidth: 1000,
          maxHeight: 800 // Default fallback
        }
    }
  })

  // Get input styling based on who can edit the field to match the overlay colors
  const getInputClassForFieldType = computed(() => {
    const truncateClass = props.fieldValue ? '' : 'truncate'
    const rightPadding = shouldShowErrorIcon.value ? 'pr-6' : 'pr-2'
    const baseClasses = `h-full w-full m-0 pl-2 ${rightPadding} py-1 ${truncateClass} outline-none rounded-sm`

    // For checkbox, use a simplified class set
    if (props.field.type === FormFieldTypeEnum.CHECKBOX) {
      if (hasValidationError.value || isRequiredAndEmpty.value) {
        return 'outline-none border-2 border-red-500 focus:ring-2 focus:ring-red-500 focus:ring-offset-1'
      }
      switch (props.field.assigned_to) {
        case 'lawyer':
          return 'outline-none border border-blue-500 focus:ring-2 focus:ring-blue-500 focus:ring-offset-1'
        case 'client':
          return 'outline-none border border-purple-500 focus:ring-2 focus:ring-purple-500 focus:ring-offset-1'
        default:
          return 'outline-none border border-gray-500 focus:ring-2 focus:ring-gray-500 focus:ring-offset-1'
      }
    }

    if (hasValidationError.value || isRequiredAndEmpty.value) {
      return `${baseClasses} border border-red-500 bg-red-50/30 focus:bg-red-50/50 focus:border-red-600 focus:ring-2 focus:ring-red-500 focus:ring-offset-1 text-gray-800 font-medium placeholder:text-gray-400 placeholder:font-normal placeholder:truncate`
    }

    switch (props.field.assigned_to) {
      case 'lawyer':
        return `${baseClasses} border border-blue-500 bg-blue-50/30 focus:bg-blue-50/50 focus:border-blue-600 focus:ring-2 focus:ring-blue-500 focus:ring-offset-1 text-gray-800 font-medium placeholder:text-gray-400 placeholder:font-normal placeholder:truncate`
      case 'client':
        return `${baseClasses} border border-purple-500 bg-purple-50/30 focus:bg-purple-50/50 focus:border-purple-600 focus:ring-2 focus:ring-purple-500 focus:ring-offset-1 text-gray-800 font-medium placeholder:text-gray-400 placeholder:font-normal placeholder:truncate`
      default:
        return `${baseClasses} border border-gray-500 bg-gray-50/30 focus:bg-gray-50/50 focus:border-gray-600 focus:ring-2 focus:ring-gray-500 focus:ring-offset-1 text-gray-800 font-medium placeholder:text-gray-400 placeholder:font-normal placeholder:truncate`
    }
  })

  // Get container styling for checkbox and signature fields based on who can edit them
  const getContainerClassForFieldType = computed(() => {
    const baseCheckboxClasses =
      'flex h-full w-full items-center justify-center rounded-sm shadow-sm'
    const baseSignatureClasses =
      'flex h-full w-full cursor-pointer items-center justify-center rounded-sm border-2 border-dashed transition-colors duration-200 shadow-sm'

    switch (props.field.type) {
      case FormFieldTypeEnum.CHECKBOX:
        if (hasValidationError.value || isRequiredAndEmpty.value) {
          return `${baseCheckboxClasses} bg-red-50/30 border-2 border-red-400`
        }
        switch (props.field.assigned_to) {
          case 'lawyer':
            return `${baseCheckboxClasses} bg-blue-50/30 border border-blue-300/80`
          case 'client':
            return `${baseCheckboxClasses} bg-purple-50/30 border border-purple-300/80`
          default:
            return `${baseCheckboxClasses} bg-gray-50/30 border border-gray-300/80`
        }
      case FormFieldTypeEnum.SIGNATURE:
        if (hasValidationError.value || isRequiredAndEmpty.value) {
          return `${baseSignatureClasses} border-red-500 bg-red-50/30 hover:bg-red-50/50`
        }
        switch (props.field.assigned_to) {
          case 'lawyer':
            return `${baseSignatureClasses} border-blue-400/80 bg-blue-50/30 hover:bg-blue-50/50`
          case 'client':
            return `${baseSignatureClasses} border-purple-400/80 bg-purple-50/30 hover:bg-purple-50/50`
          default:
            return `${baseSignatureClasses} border-gray-400/80 bg-gray-50/30 hover:bg-gray-50/50`
        }
      default:
        return ''
    }
  })

  const handleKeyDown = (e: KeyboardEvent) => {
    if (e.key === 'Shift') {
      isShiftPressed.value = true
    }
  }

  const handleKeyUp = (e: KeyboardEvent) => {
    if (e.key === 'Shift') {
      isShiftPressed.value = false
      // Reset axis to both when shift is released
      dragAxis.value = 'both'
    }
  }

  const handleDragging = (left: number, top: number) => {
    // Only set initial position on first drag event
    if (!initialDragPosition.value) {
      initialDragPosition.value = {
        x: props.field.coordinates.x * props.zoomLevel,
        y: props.field.coordinates.y * props.zoomLevel
      }
      dragAxis.value = 'both'
      emit('dragstart', props.field)
    }

    // Update element position
    elementX.value = left
    elementY.value = top
  }

  const handleDragStop = () => {
    initialDragPosition.value = null
    dragAxis.value = 'both'
    emit('dragend')
  }

  const handleResizing = (left: number, top: number, width: number, height: number) => {
    // Update element size and position
    elementX.value = left
    elementY.value = top
    elementWidth.value = width
    elementHeight.value = height
  }

  const handleResizeStop = () => {
    emit('resizeend', props.field)
  }

  const handleClick = () => {
    if (props.isFillable && props.field.type === FormFieldTypeEnum.SIGNATURE) {
      emit('signature-click', props.field)
    } else if (props.isEditable) {
      // Only emit regular click for editable mode
      emit('click', props.field)
    }
  }

  const handleFieldValueChange = (value: any) => {
    if (props.field.uuid) {
      emit('field-value-change', props.field.uuid, value)
    }
  }

  const shouldShowField = computed(() => {
    // show all if isFilledBy not set
    if (!props.isFilledBy) {
      return true
    }

    // For signature fields, use exact assignment only
    if (props.field.type === FormFieldTypeEnum.SIGNATURE) {
      return props.isFilledBy === props.field.assigned_to
    }

    // For all other field types, lawyers can see everything, others see only their assigned fields
    return props.isFilledBy === 'lawyer' || props.isFilledBy === props.field.assigned_to
  })

  const shouldShowInteractiveField = computed(() => {
    return (
      props.isFillable &&
      [
        FormFieldTypeEnum.TEXTINPUT,
        FormFieldTypeEnum.TEXTAREA,
        FormFieldTypeEnum.DATEFIELD,
        FormFieldTypeEnum.CHECKBOX,
        FormFieldTypeEnum.SIGNATURE
      ].includes(props.field.type)
    )
  })

  watch(
    () => props.field.coordinates,
    (newCoords) => {
      elementX.value = newCoords.x * props.zoomLevel
      elementY.value = newCoords.y * props.zoomLevel
      elementWidth.value = newCoords.width * props.zoomLevel
      elementHeight.value = newCoords.height * props.zoomLevel
    },
    { deep: true }
  )

  watch(
    () => props.zoomLevel,
    (newZoom) => {
      elementX.value = props.field.coordinates.x * newZoom
      elementY.value = props.field.coordinates.y * newZoom
      elementWidth.value = props.field.coordinates.width * newZoom
      elementHeight.value = props.field.coordinates.height * newZoom
    }
  )

  watch(
    () => props.isSelected,
    (newSelected) => {
      elementActive.value = newSelected
    },
    { immediate: true }
  )

  watch([elementX, elementY], ([newX, newY]) => {
    const pdfX = newX / props.zoomLevel
    const pdfY = newY / props.zoomLevel
    emit('move', props.field, { x: pdfX, y: pdfY })

    if (isShiftPressed.value && dragAxis.value === 'both' && initialDragPosition.value) {
      const deltaX = Math.abs(newX - initialDragPosition.value.x)
      const deltaY = Math.abs(newY - initialDragPosition.value.y)

      if (deltaX > 5 || deltaY > 5) {
        dragAxis.value = deltaX > deltaY ? 'x' : 'y'
      }
    }

    const tempField = {
      ...props.field,
      coordinates: {
        ...props.field.coordinates,
        x: pdfX,
        y: pdfY
      }
    }
    emit('dragmove', tempField)
  })

  watch([elementWidth, elementHeight], ([newWidth, newHeight]) => {
    const pdfWidth = newWidth / props.zoomLevel
    const pdfHeight = newHeight / props.zoomLevel
    emit('resize', props.field, { width: pdfWidth, height: pdfHeight })
  })

  onMounted(() => {
    window.addEventListener('keydown', handleKeyDown)
    window.addEventListener('keyup', handleKeyUp)
  })

  onUnmounted(() => {
    window.removeEventListener('keydown', handleKeyDown)
    window.removeEventListener('keyup', handleKeyUp)

    // Clean up drag state
    initialDragPosition.value = null
    dragAxis.value = 'both'
  })
</script>

<template>
  <div
    v-if="shouldShowField"
    style="position: absolute; top: 0; left: 0; width: 100%; height: 100%; pointer-events: none"
  >
    <VueDraggableResizable
      ref="draggableResizableRef"
      :key="`${field.uuid}-${field.type}`"
      :x="elementX"
      :y="elementY"
      :w="elementWidth"
      :h="elementHeight"
      :active="elementActive"
      :prevent-deactivation="preventDeactivation"
      :min-width="fieldSize.minWidth * zoomLevel"
      :min-height="fieldSize.minHeight * zoomLevel"
      :max-width="fieldSize.maxWidth * zoomLevel"
      :max-height="fieldSize.maxHeight * zoomLevel"
      :draggable="isEditable"
      :resizable="isEditable"
      :handles="['tr', 'tl', 'br', 'bl']"
      :axis="dragAxis"
      style="pointer-events: auto"
      @dragging="handleDragging"
      @drag-stop="handleDragStop"
      @resizing="handleResizing"
      @resize-stop="handleResizeStop"
      @activated="handleClick"
    >
      <!-- Interactive field content when fillable -->
      <template v-if="shouldShowInteractiveField">
        <!-- Text fields -->
        <div
          v-if="field.type === FormFieldTypeEnum.TEXTINPUT"
          class="relative flex h-full w-full items-center"
        >
          <input
            :name="field.uuid || 'text-field'"
            :value="fieldValue"
            :placeholder="field.placeholder || 'Enter text'"
            type="text"
            :class="getInputClassForFieldType"
            :style="textInputStyle"
            @input="handleFieldValueChange(($event.target as HTMLInputElement).value)"
          />
        </div>

        <!-- Textarea fields -->
        <div
          v-else-if="field.type === FormFieldTypeEnum.TEXTAREA"
          class="relative flex h-full w-full items-stretch"
        >
          <textarea
            :name="field.uuid || 'textarea-field'"
            :value="fieldValue"
            :placeholder="field.placeholder || 'Enter text'"
            :class="getInputClassForFieldType + ' !resize-none'"
            :style="textInputStyle"
            @input="handleFieldValueChange(($event.target as HTMLTextAreaElement).value)"
          ></textarea>
        </div>

        <!-- Date fields -->
        <div
          v-else-if="field.type === FormFieldTypeEnum.DATEFIELD"
          :class="getInputClassForFieldType + ' !flex !h-full !items-center !justify-start !p-0'"
          :style="dateFieldContainerStyle"
        >
          <DatePickerField
            :name="field.uuid || 'date-field'"
            :model-value="fieldValue"
            @update:model-value="handleFieldValueChange"
          />
        </div>

        <!-- Checkbox fields -->
        <div
          v-else-if="field.type === FormFieldTypeEnum.CHECKBOX"
          :class="getContainerClassForFieldType"
          :style="checkboxStyle"
        >
          <CheckboxField
            :name="field.uuid || 'checkbox-field'"
            :model-value="fieldValue"
            :label="field.placeholder || ''"
            :class="getInputClassForFieldType"
            @update:model-value="handleFieldValueChange"
          />
        </div>

        <!-- Signature fields -->
        <div
          v-else-if="field.type === FormFieldTypeEnum.SIGNATURE"
          :class="getContainerClassForFieldType"
          @click="() => emit('signature-click', field)"
        >
          <!-- Show signature image if available -->
          <img
            v-if="signatureData"
            :src="signatureData"
            alt="Signature"
            class="h-full w-full object-contain"
          />
          <!-- Show placeholder text if no signature -->
          <span
            v-else
            :class="[
              'select-none font-medium',
              {
                'text-red-600': hasValidationError || isRequiredAndEmpty,
                'text-blue-600/80':
                  !hasValidationError && !isRequiredAndEmpty && field.assigned_to === 'lawyer',
                'text-purple-600/80':
                  !hasValidationError && !isRequiredAndEmpty && field.assigned_to === 'client',
                'text-gray-600/80':
                  !hasValidationError &&
                  !isRequiredAndEmpty &&
                  !['lawyer', 'client'].includes(field.assigned_to)
              }
            ]"
            :style="overlayTextStyle"
          >
            Click to sign
          </span>
        </div>
        <!-- Validation error icon with tooltip -->
        <TooltipProvider
          v-if="shouldShowErrorIcon"
          :delay-duration="200"
        >
          <Tooltip>
            <TooltipTrigger as-child>
              <div
                class="absolute right-1 z-50"
                :class="
                  field.type === FormFieldTypeEnum.TEXTAREA ? 'top-1' : 'top-1/2 -translate-y-1/2'
                "
              >
                <CircleAlert
                  class="h-4 w-4 text-red-500"
                  :stroke-width="2.5"
                />
              </div>
            </TooltipTrigger>
            <TooltipContent
              class="max-w-xs bg-red-500 text-white"
              :side="'top'"
            >
              {{ validationError }}
            </TooltipContent>
          </Tooltip>
        </TooltipProvider>
      </template>

      <!-- Regular field overlay when not fillable -->
      <template v-else>
        <div
          :class="[
            fieldColorClasses,
            'flex h-full w-full cursor-move items-center',
            {
              'cursor-pointer': !isEditable,
              'px-2': field.type !== FormFieldTypeEnum.CHECKBOX,
              'justify-center': field.type === FormFieldTypeEnum.CHECKBOX
            }
          ]"
        >
          <component
            :is="IconComponent"
            v-if="field.type !== FormFieldTypeEnum.CHECKBOX"
            class="pointer-events-none mr-1 h-3 w-3 flex-shrink-0 text-black"
          />

          <div
            v-if="shouldShowText"
            class="flex min-w-0 flex-1 items-center"
          >
            <span
              class="pointer-events-none truncate font-normal text-black"
              :style="overlayTextStyle"
            >
              {{ displayText }}
            </span>
            <span
              v-if="field.required"
              class="pointer-events-none ml-[1px] flex-shrink-0 font-medium text-red-500"
              :style="overlayTextStyle"
            >
              *
            </span>
          </div>
        </div>
      </template>

      <!-- Custom resize handles that match field style -->
      <template #tl>
        <div
          :class="handleClasses"
          title="Resize field"
        />
      </template>
      <template #tr>
        <div
          :class="handleClasses"
          title="Resize field"
        />
      </template>
      <template #bl>
        <div
          :class="handleClasses"
          title="Resize field"
        />
      </template>
      <template #br>
        <div
          :class="handleClasses"
          title="Resize field"
        />
      </template>
    </VueDraggableResizable>
  </div>
</template>

<style scoped>
  :deep(.vdr) {
    border: none !important;
    box-sizing: border-box !important;
  }
  :deep(.handle) {
    border: none !important;
  }
  :deep(.handle-tl),
  :deep(.handle-tr),
  :deep(.handle-bl),
  :deep(.handle-br) {
    border: none !important;
  }

  /* Override DatePickerField styling for better overlay appearance */
  :deep(.relative) {
    height: 100% !important;
    width: 100% !important;
    box-sizing: border-box !important;
  }

  :deep(.relative .relative.w-full) {
    height: 100% !important;
    box-sizing: border-box !important;
  }

  :deep(.relative input[type='date']) {
    font-size: var(--date-field-font-size, 10px) !important;
    padding: 2px 4px 2px var(--date-field-icon-spacing, 20px) !important;
    height: 100% !important;
    min-height: 0 !important;
    border: none !important;
    background: transparent !important;
    line-height: var(--date-field-line-height, 1.2) !important;
    border-radius: 0 !important;
    box-sizing: border-box !important;
  }

  :deep(.relative .absolute.left-3) {
    left: 2px !important;
    top: 50% !important;
    transform: translateY(-50%) !important;
    z-index: 10 !important;
  }

  :deep(.relative .absolute svg) {
    width: var(--date-field-icon-size, 12px) !important;
    height: var(--date-field-icon-size, 12px) !important;
  }

  /* Ensure input fields are properly positioned within their containers */
  :deep(input[type='text']),
  :deep(input[type='date']) {
    box-sizing: border-box !important;
    position: relative !important;
  }

  /* Ensure textarea fields are properly positioned and sized */
  textarea {
    box-sizing: border-box !important;
    position: relative !important;
  }

  /* Ensure placeholder text is truncated for input and textarea */
  input::placeholder,
  textarea::placeholder {
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  }

  /* Scale checkbox input to fit field size */
  :deep(input[type='checkbox']) {
    width: var(--checkbox-size, 20px) !important;
    height: var(--checkbox-size, 20px) !important;
    min-width: var(--checkbox-size, 20px) !important;
    min-height: var(--checkbox-size, 20px) !important;
    flex-shrink: 0;
  }

  /* Scale checkbox label font size */
  :deep(.flex.items-center label) {
    font-size: var(--checkbox-font-size, 14px) !important;
    margin-left: calc(var(--checkbox-size, 20px) * 0.1) !important;
  }
</style>
